<template>
  <v-dialog :model-value="modelValue" persistent max-width="700" :fullscreen="$store.state.isMobile">
    <v-card>
      <v-card-title class="d-flex align-center">Save Your Recovery Code <encryption-gen-badge generation="new" /></v-card-title>
      <v-card-text>
        <div class="text-subtitle-1 mb-3">
          Your account's screenshots are protected with end-to-end encryption.
          This recovery code is one of the ways to unlock them on a new device.
        </div>
        <v-alert type="warning" variant="tonal" density="compact" class="mb-5">
          If you lose access to all of your devices{{ passkeyRegistered ? ' and your passkey' : '' }},
          this code is the <strong>only</strong> way to recover your encrypted data.
          We cannot recover it for you. Store it somewhere safe like a password
          manager, or printed and locked away.
        </v-alert>

        <v-card variant="outlined" class="pa-4 mb-4 text-center">
          <div class="recovery-code text-h6">{{ code }}</div>
        </v-card>

        <div class="d-flex justify-center mb-5" style="gap: 8px;">
          <v-btn variant="outlined" size="small" @click="copy">
            <v-icon start>content_copy</v-icon>{{ copied ? 'Copied!' : 'Copy' }}
          </v-btn>
          <v-btn variant="outlined" size="small" @click="download">
            <!-- `file_download`: the classic Material Icons ligature — plain
                 `download` only exists in newer font releases than ours. -->
            <v-icon start>file_download</v-icon>Download
          </v-btn>
        </div>

        <v-checkbox
          v-model="acknowledged"
          color="primary"
          density="compact"
          hide-details
          label="I have saved my recovery code somewhere safe"
        />
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          variant="elevated"
          color="primary"
          :disabled="!acknowledged"
          @click="finish"
        >
          Continue
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
  import fileDownload from 'js-file-download'
  import { copyText } from '@/services/ClipboardHelper'

  // Blocking require-acknowledge display of the NC1 recovery code, shown once
  // right after the signup bootstrap creates the identity. The code exists
  // only in this component's props — it is never persisted anywhere.
  export default {
    name: 'RecoveryCodeDialog',
    props: {
      modelValue: {
        type: Boolean,
        default: false
      },
      code: {
        type: String,
        default: ''
      },
      passkeyRegistered: {
        type: Boolean,
        default: false
      }
    },
    emits: ['update:modelValue', 'acknowledged'],
    data() {
      return {
        acknowledged: false,
        copied: false
      }
    },
    methods: {
      async copy() {
        this.copied = await copyText(this.code)
        if(this.copied) {
          setTimeout(() => { this.copied = false }, 2000)
        }
      },
      download() {
        const brand = process.env.VUE_APP_NAME || 'account'
        fileDownload(
          `${brand} end-to-end encryption recovery code\n\n${this.code}\n\nKeep this file somewhere safe. Anyone with this code can decrypt your data; without it (or another signed-in device or passkey) your encrypted data cannot be recovered.\n`,
          `${brand}-recovery-code.txt`
        )
      },
      finish() {
        this.$emit('update:modelValue', false)
        this.$emit('acknowledged')
      }
    }
  }
</script>

<style scoped>
  .recovery-code {
    font-family: monospace;
    letter-spacing: 0.08em;
    word-break: break-all;
    user-select: all;
  }
</style>
